/**
 * @file GraphicsQualityService.ts
 * @role Resolves the persisted Auto/High/Balanced/Low choice into one validated concrete
 *       presentation profile and lets Auto step down after a sustained slow-frame window.
 * @owns Profile validation, capability classification, slow-frame sampling, and notifications.
 * @neverDoes Touch Pixi, assets, gameplay state, the RGS, or authoritative result data.
 */
import { z } from 'zod';
import graphicsQualityConfigRaw from '@config/graphics-quality.config.json';
import { GameError } from '@core/errors/GameError';

export const GraphicsQualitySettingSchema = z.enum(['auto', 'high', 'balanced', 'low']);
export const ConcreteGraphicsQualitySchema = z.enum(['high', 'balanced', 'low']);

const ProfileSchema = z.object({
  maxDevicePixelRatio: z.number().min(0.5).max(3),
  antialias: z.boolean(),
  effectsQuality: z.enum(['high', 'medium', 'low']),
  particleMultiplier: z.number().min(0).max(1),
  ambientMultiplier: z.number().min(0).max(1),
  motionTextures: z.boolean(),
  maxFps: z.number().int().min(15).max(120),
});

export const GraphicsQualityConfigSchema = z.object({
  profiles: z.object({
    high: ProfileSchema,
    balanced: ProfileSchema,
    low: ProfileSchema,
  }),
  auto: z.object({
    slowFrameThresholdMs: z.number().positive(),
    sampleFrames: z.number().int().positive(),
    slowFrameRatio: z.number().min(0).max(1),
    highMinDeviceMemoryGb: z.number().positive(),
    highMinHardwareConcurrency: z.number().int().positive(),
    lowMaxDeviceMemoryGb: z.number().positive(),
    lowMaxHardwareConcurrency: z.number().int().positive(),
  }),
});

export type GraphicsQualitySetting = z.infer<typeof GraphicsQualitySettingSchema>;
export type ConcreteGraphicsQuality = z.infer<typeof ConcreteGraphicsQualitySchema>;
export type GraphicsQualityConfig = z.infer<typeof GraphicsQualityConfigSchema>;
export type GraphicsQualityProfile = z.infer<typeof ProfileSchema> & {
  readonly id: ConcreteGraphicsQuality;
};

export interface DeviceCapabilities {
  readonly deviceMemoryGb?: number;
  readonly hardwareConcurrency?: number;
}

type QualityListener = (profile: GraphicsQualityProfile) => void;

function browserCapabilities(): DeviceCapabilities {
  if (typeof navigator === 'undefined') return {};
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  return {
    ...(typeof memory === 'number' ? { deviceMemoryGb: memory } : {}),
    ...(typeof navigator.hardwareConcurrency === 'number'
      ? { hardwareConcurrency: navigator.hardwareConcurrency }
      : {}),
  };
}

export function validateGraphicsQualityConfig(raw: unknown): GraphicsQualityConfig {
  const parsed = GraphicsQualityConfigSchema.safeParse(raw);
  if (!parsed.success) {
    throw new GameError(
      'CONFIG_LOAD_FAILED',
      `Invalid graphics-quality config: ${parsed.error.message}`,
      {
        cause: parsed.error,
      },
    );
  }
  return parsed.data;
}

export class GraphicsQualityService {
  private readonly config: GraphicsQualityConfig;
  private readonly listeners = new Set<QualityListener>();
  private selected: GraphicsQualitySetting = 'auto';
  private active: ConcreteGraphicsQuality = 'balanced';
  private sampledFrames = 0;
  private slowFrames = 0;

  constructor(
    raw: unknown = graphicsQualityConfigRaw,
    private readonly capabilities: DeviceCapabilities = browserCapabilities(),
  ) {
    this.config = validateGraphicsQualityConfig(raw);
    this.active = this.resolveAutoProfile();
  }

  select(setting: GraphicsQualitySetting): void {
    if (setting === this.selected) return;
    const next = setting === 'auto' ? this.resolveAutoProfile() : setting;
    const changed = next !== this.active;
    this.selected = setting;
    this.active = next;
    this.resetSample();
    if (changed) this.notify();
  }

  observeFrame(deltaMs: number): void {
    if (this.selected !== 'auto' || this.active === 'low' || deltaMs <= 0) return;
    this.sampledFrames += 1;
    if (deltaMs >= this.config.auto.slowFrameThresholdMs) this.slowFrames += 1;
    if (this.sampledFrames < this.config.auto.sampleFrames) return;

    const sustainedSlow = this.slowFrames / this.sampledFrames >= this.config.auto.slowFrameRatio;
    this.resetSample();
    if (!sustainedSlow) return;
    this.active = this.active === 'high' ? 'balanced' : 'low';
    this.notify();
  }

  get selection(): GraphicsQualitySetting {
    return this.selected;
  }

  get profile(): GraphicsQualityProfile {
    return { id: this.active, ...this.config.profiles[this.active] };
  }

  subscribe(listener: QualityListener): () => void {
    this.listeners.add(listener);
    listener(this.profile);
    return () => this.listeners.delete(listener);
  }

  private resolveAutoProfile(): ConcreteGraphicsQuality {
    const { deviceMemoryGb, hardwareConcurrency } = this.capabilities;
    const auto = this.config.auto;
    if (
      (deviceMemoryGb !== undefined && deviceMemoryGb <= auto.lowMaxDeviceMemoryGb) ||
      (hardwareConcurrency !== undefined && hardwareConcurrency <= auto.lowMaxHardwareConcurrency)
    ) {
      return 'low';
    }
    if (
      deviceMemoryGb !== undefined &&
      hardwareConcurrency !== undefined &&
      deviceMemoryGb >= auto.highMinDeviceMemoryGb &&
      hardwareConcurrency >= auto.highMinHardwareConcurrency
    ) {
      return 'high';
    }
    return 'balanced';
  }

  private resetSample(): void {
    this.sampledFrames = 0;
    this.slowFrames = 0;
  }

  private notify(): void {
    const profile = this.profile;
    for (const listener of [...this.listeners]) listener(profile);
  }
}
